import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import { useSessionStore } from '../store/session';
import { useChannelStore } from '../store/channel';

const createBaseClient = (
  headers: Record<string, string> = {},
): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.GATSBY_API_URL,
    timeout: 25000,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      ...headers,
    },
  });

  axiosRetry(instance, { retries: 2, retryDelay: axiosRetry.exponentialDelay });

  instance.interceptors.request.use((config: AxiosRequestConfig) => {
    const { channel } = useChannelStore.getState();
    if (channel) {
      config.headers.channel = channel;
    }
    return config;
  });

  return instance;
};

const createPrivateClient = (
  headers: Record<string, string> = {},
): AxiosInstance => {
  const instance = createBaseClient(headers);

  instance.interceptors.request.use((config: AxiosRequestConfig) => {
    const { sessionId } = useSessionStore.getState();
    if (sessionId) {
      config.headers.Authorization = `customer ${sessionId}`;
    }
    return config;
  });

  return instance;
};

let unauthorizedHandler: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void): void => {
  unauthorizedHandler = handler;
};

const createPrivateClientWithInterceptors = (
  headers: Record<string, string> = {},
): AxiosInstance => {
  const instance = createPrivateClient(headers);

  instance.interceptors.response.use(
    response => response,
    error => {
      if (error?.response?.status === 401 && unauthorizedHandler) {
        const { sessionId } = useSessionStore.getState();
        if (sessionId) {
          unauthorizedHandler();
        }
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

const api = createBaseClient();
const privateApi = createPrivateClientWithInterceptors();

export { api, privateApi, createBaseClient, createPrivateClient };
